import { z } from 'zod';
import {
  changeRegistrationEventFilterInputSchema,
  webhookDeliveryContextSchema,
  webhookDeliveryPayloadSchema,
} from './change-registration';
import { STORABLE_EVENT_TYPE_NAMES } from './events';
import { ulidSchema } from './id';

/**
 * `GET /me/events` — the pull alternative to change registrations (webhooks),
 * for a connected tool with no public URL. Decision record:
 * `docs/notes/event-poll-plan.md`. Consumer guide:
 * `docs/notes/event-poll-consumer-guide.md`.
 *
 * Every schema here is a COPY. Nothing shipped is widened in place: the event
 * shape is derived from `webhookDeliveryPayloadSchema.shape.activityEntry` with
 * `boardId` narrowed to non-null and a `context` added, the context is a
 * `.pick` of `webhookDeliveryContextSchema`, and the `types` alphabet is
 * derived from `changeRegistrationEventFilterInputSchema` with `.min`/`.max`,
 * each of which returns a new schema. The webhook body itself is unchanged.
 */

export const MEMBER_EVENTS_LIMIT_DEFAULT = 500;
export const MEMBER_EVENTS_LIMIT_MAX = 1000;
export const MEMBER_EVENTS_BOARD_IDS_MAX = 100;

/**
 * D8 — how far back one call may reach. ⚠ A QUERY-COST POLICY, NOT A RETENTION
 * HORIZON: activity is kept forever (`03-collaboration.md`) and nothing is
 * pruned. Do not build a pruner on the strength of this number.
 */
export const MEMBER_EVENTS_CURSOR_MAX_AGE_HOURS = 72;

/** D8's machine-readable reason, under `error.details.updatedSince`. */
export const MEMBER_EVENTS_CURSOR_EXPIRED = 'cursor_expired';

/**
 * A timestamp Zod accepts but the arithmetic cannot represent (before 1970 or
 * after 9999-12-31), under `error.details.updatedSince`. Without this refusal
 * `0000-01-01T00:00:00Z` reached Postgres and a year-10000 cursor broke the
 * response schema — both 500s (plan §14, O-3).
 */
export const MEMBER_EVENTS_CURSOR_OUT_OF_RANGE = 'out_of_range';

// Module-private copy, the house pattern (calendar-view.ts, table-view.ts).
// Fastify's parser gives `?k=A` → 'A' and `?k=A&k=B` → ['A','B']
// (board-filter.ts:252-307), so a bare `z.array` would refuse the single value.
function repeatedQueryValue(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return Array.from(value as unknown[]);
  return [value];
}

export const memberEventsQuerySchema = z.strictObject({
  updatedSince: z.iso
    .datetime({ offset: true })
    .describe(
      'Inclusive lower bound (>=), ISO-8601 with Z or an offset (percent-encode a + as %2B). ' +
        'Echo the previous response’s nextUpdatedSince verbatim; never build it from an ' +
        'event’s createdAt. Older than 72 hours is refused with cursor_expired.',
    ),
  types: z
    .preprocess(
      repeatedQueryValue,
      changeRegistrationEventFilterInputSchema
        .min(1)
        .max(STORABLE_EVENT_TYPE_NAMES.length),
    )
    .optional()
    .describe(
      'Only these event types. Repeat the key for several. Changing the set starts a new cursor chain.',
    ),
  boardIds: z
    .preprocess(
      repeatedQueryValue,
      z.array(ulidSchema).min(1).max(MEMBER_EVENTS_BOARD_IDS_MAX),
    )
    .optional()
    .describe(
      'Only these boards, up to 100. Repeat the key for several. A board you are not a member ' +
        'of contributes nothing and is not an error. Changing the set starts a new cursor chain.',
    ),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MEMBER_EVENTS_LIMIT_MAX)
    .default(MEMBER_EVENTS_LIMIT_DEFAULT)
    .describe(
      '1 to 1000, default 500. A page can hold MORE than this: it never ends inside a ' +
        'millisecond. Loop while hasMore is true.',
    ),
});
export type MemberEventsQuery = z.infer<typeof memberEventsQuerySchema>;

/**
 * The per-event context — the webhook's `context.card` and
 * `context.mentionedUserIds`, and nothing else (D4, amended 2026-10-08).
 *
 * ⚠ `.pick`, so both entries ARE the webhook's own schema objects: the card
 * shape cannot fork between the two surfaces (pinned by identity in
 * `member-events.test.ts`). The values are resolved by the same SQL fragment
 * as the webhook's (`eventContextColumns` in
 * `apps/server/src/external/webhook-delivery.ts`), when the PAGE IS READ:
 *
 *   - `card` is `null` exactly when `cardId` is `null`. `memberUserIds` and
 *     `creatorUserId` are read at poll time, so re-reading an old row shows the
 *     card's members as they are now, not as they were — and only while the
 *     card is still on the event's board: a card moved (or mirrored) to another
 *     board answers `[]` and `null` on the rows it left, so a reader of that
 *     board learns nothing it could not read.
 *   - `card.ref` is the card's reference as THE CALLER may use it (the webhook
 *     resolves it for the registration's authorising member), so it is the one
 *     value that can differ between the two surfaces for the same event.
 *   - `mentionedUserIds` is the recipients of the `mentioned` notification rows
 *     this event wrote — sorted, `[]` when nobody, never absent. On
 *     `comment.edited` that is the NEWLY mentioned only, and the generate.ts
 *     ruling against a payload key stands: nothing is written to
 *     `activity_entries` for it.
 *
 * `authorisingUserId` and `resolvedAt` are left out on purpose: the poll has no
 * registration whose grant could authorise anything (the caller is the
 * session), and the read time is the response's own.
 */
export const memberEventContextSchema = webhookDeliveryContextSchema.pick({
  card: true,
  mentionedUserIds: true,
});
export type MemberEventContext = z.infer<typeof memberEventContextSchema>;

/**
 * One event: the webhook's `activityEntry` fields PLUS `context` (D4, amended
 * 2026-10-08 — the webhook body is `{ activityEntry, context }`, and a poll row
 * is that body flattened one level). A derived copy: `boardId` is narrowed to
 * non-null because an event with no board is never returned (D2). The shipped
 * schema still accepts null.
 *
 * ⚠ `context` is REQUIRED, the webhook's own choice for the same block: a
 * producer that forgets it is a compile error at `toMemberEvent`'s annotated
 * literal and a 500 from the serialiser, never a row quietly without it.
 */
export const memberEventSchema = webhookDeliveryPayloadSchema.shape.activityEntry.extend({
  boardId: ulidSchema,
  context: memberEventContextSchema,
});
export type MemberEvent = z.infer<typeof memberEventSchema>;

// ⚠ Response schema: NO preprocess or transform. The serialiser encodes with
// this schema and a mismatch is a 500 (fastify-type-provider-zod core.cjs).
export const memberEventsPageSchema = z.object({
  events: z.array(memberEventSchema),
  nextUpdatedSince: z.iso.datetime(),
  hasMore: z.boolean(),
});
export type MemberEventsPage = z.infer<typeof memberEventsPageSchema>;
